/**
 * Förhandlingsöverenskommelse om årlig hyreshöjning.
 *
 * The agreement that follows from a förhandlingsordning: the year's rent
 * increase, either as one percentage for every apartment in the house or as a
 * per-apartment table, plus optional IMD tariffs and who notifies the tenants.
 *
 * Unlike the förhandlingsordning, this document's points are numbered
 * sequentially — the three-part variant simply has one more party ahead of
 * them — so these blocks use automatic numbering and the point numbers fall
 * out of composition order.
 *
 * Every point uses the compact clause heading, as in the förhandlingsordning
 * (one heading style for the whole family's points).
 *
 * Legal text verbatim from the Haymanot-approved Word template.
 */
import { br, group, nodes, optionList, option, p, section, t, table, val } from '@hyranai/regta-document-ir';
import { str } from '../lib.js';
import { dateVal, pickCode, propertyListSlot, selectSlot, slotNumber, slotText, textVal, ticked, whenOption } from '../common/index.js';
import { optionByCode } from '../forhandlingsordning/family.js';
const LEGAL = {
    plane: 'legal',
    status: 'published',
    language: 'sv-SE',
    legalReview: { reviewedBy: 'Haymanot', source: 'base-templates/Förhandlingsöverenskommelse (Haymanot-approved)' },
};
/** Beta contracts store the ADR-0062 code; the legacy lane stored `value`. */
export const INCREASE_TYPE_OPTIONS = [
    {
        code: 'samtliga_lagenheter',
        value: 'Samtliga lägenheter',
        label: 'Samma höjning för samtliga lägenheter (rekommenderas)',
        summary: 'A single percentage increase applies to every apartment in the house.',
    },
    {
        code: 'per_lagenhet',
        value: 'Per lägenhet',
        label: 'Höjning per lägenhet',
        summary: 'Increase is set individually per apartment via a table.',
    },
];
const SAMTLIGA = optionByCode(INCREASE_TYPE_OPTIONS, 'samtliga_lagenheter');
const PER_LAGENHET = optionByCode(INCREASE_TYPE_OPTIONS, 'per_lagenhet');
export const NOTICE_OPTIONS = [
    {
        code: 'hyresvard',
        value: 'Hyresvärd',
        label: 'Hyresvärden underrättar hyresgästerna',
        summary: 'The landlord alone notifies the tenants of the agreement.',
    },
    {
        code: 'gemensam_skrivelse',
        value: 'Gemensam skrivelse',
        label: 'Hyresvärden och Hyresgästorganisationen i gemensam skrivelse',
        summary: 'Landlord and tenant organisation jointly notify the tenants in one letter.',
    },
];
/** Punkt "Hus" — the properties the agreement applies to, defined as "Huset". */
export const hus = {
    ...LEGAL,
    blockKey: 'fok.hus',
    version: 'v1',
    title: 'Hus',
    summary: 'The properties and addresses the agreement covers, defined as "Huset".',
    slots: [
        propertyListSlot('property_designations', 'Fastighetsbeteckningar', 'designations', { required: true, statutory: true }),
        propertyListSlot('property_addresses', 'Samtliga adresser på fastigheterna', 'addresses', {
            required: true,
            statutory: true,
            help: 'En adress per rad. Hämtas automatiskt från länkade fastigheter.',
        }),
    ],
    compile: (input) => [
        section('fok-hus', 'Hus', [
            p([t('Fastighet/fastigheter:'), br(), textVal(input, 'property_designations')]),
            p([t('med följande adresser:'), br(), textVal(input, 'property_addresses'), t(' ("Huset")')]),
        ], { keepTogether: true, variant: 'clause' }),
    ],
};
/**
 * Punkt "Överenskommelse" — the operative point.
 *
 * Both increase forms print as checkbox alternatives, because that is how the
 * approved form reads: the unchosen alternative stays visible and unticked.
 * The per-apartment list is authored as free text ("101 — 4,5 %") and parsed
 * into a two-column table here, so the compiler owns the parsing and the
 * persisted document owns only the resulting rows.
 */
export const overenskommelse = {
    ...LEGAL,
    blockKey: 'fok.overenskommelse',
    version: 'v1',
    title: 'Överenskommelse',
    summary: 'The agreed increase — one percentage for the whole house or a per-apartment table — plus IMD tariffs and the notification duty.',
    slots: [
        selectSlot('increase_type', 'Höjningstyp', INCREASE_TYPE_OPTIONS, {
            required: true,
            statutory: true,
            default: SAMTLIGA.code,
        }),
        {
            key: 'rent_increase_percent',
            label: 'Höjning (procent)',
            type: 'number',
            min: 0,
            max: 100,
            help: 'T.ex. 4,5',
            visibleWhen: whenOption('increase_type', SAMTLIGA),
        },
        {
            key: 'effective_date',
            label: 'Höjningen gäller från',
            type: 'date',
            visibleWhen: whenOption('increase_type', SAMTLIGA),
        },
        {
            key: 'apartment_increases',
            label: 'Höjning per lägenhet',
            type: 'textarea',
            help: 'En rad per lägenhet, t.ex.\n101 — 4,5 %\n102 — 3,8 %',
            visibleWhen: whenOption('increase_type', PER_LAGENHET),
        },
        { key: 'imd_water', label: 'IMD — Varmvatten', type: 'text', help: 'Ange i kr/m³. Lämna tomt om inte tillämpligt.' },
        { key: 'imd_heat', label: 'IMD — Värme', type: 'text' },
        { key: 'imd_electricity', label: 'IMD — El', type: 'text' },
        selectSlot('notice_type_overens', 'Underrättelse till hyresgäster', NOTICE_OPTIONS, { required: true, default: 'hyresvard' }),
    ],
    compile: (input, ctx) => {
        // A draft without a choice renders the legacy defaults.
        const type = pickCode(INCREASE_TYPE_OPTIONS, input.increase_type) ?? SAMTLIGA.code;
        const isSamtliga = type === SAMTLIGA.code;
        const notice = pickCode(NOTICE_OPTIONS, input.notice_type_overens) ?? 'hyresvard';
        const percent = slotNumber(input.rent_increase_percent);
        return {
            nodes: [
                section('fok-overenskommelse', 'Överenskommelse', nodes(optionList([
                    option(ticked(ctx, isSamtliga), [
                        t('Årshyran för samtliga lägenheter i Huset höjs med '),
                        val(ctx.format.number(percent, percentDigits(percent)), {
                            raw: percent,
                            format: 'number',
                            blankWidth: 'short',
                            sourceKey: 'rent_increase_percent',
                        }),
                        t(' procent från den '),
                        dateVal(ctx, input, 'effective_date'),
                        t('.'),
                    ], { code: 'samtliga_lagenheter' }),
                    {
                        ...option(ticked(ctx, !isSamtliga), 'Årshyran för lägenheterna i Huset höjs enligt följande:', {
                            code: 'per_lagenhet',
                        }),
                        children: [apartmentTable(input, ctx.mode === 'blank')],
                    },
                ], { sourceKey: 'increase_type', select: 'single' }), imdBlock(input, ctx.mode === 'blank'), optionList([
                    option(ticked(ctx, notice === 'hyresvard'), 'Det åligger Hyresvärden att underrätta hyresgästerna om innehållet i denna överenskommelse.', { code: 'hyresvard' }),
                    option(ticked(ctx, notice === 'gemensam_skrivelse'), 'Hyresvärden och Hyresgästorganisationen underrättar hyresgästerna om innehållet i denna överenskommelse i en gemensam skrivelse.', { code: 'gemensam_skrivelse' }),
                ], { sourceKey: 'notice_type_overens', select: 'single' }), p('Om överenskommelsen p.g.a. felaktig uppgift, förbiseende eller annan liknande anledning fått ett oriktigt innehåll, ska parterna medverka till att rättelse sker snarast.', {
                    variant: 'fine_print',
                })), { variant: 'clause' }),
            ],
            diagnostics: missingIncreaseDetail(input, type, ctx.mode)
                ? [
                    {
                        severity: 'error',
                        code: 'increase_not_specified',
                        message: type === 'samtliga_lagenheter'
                            ? 'Höjningen i procent och ikraftträdandedatum måste anges.'
                            : 'Höjningen per lägenhet måste anges.',
                        slotKey: type === 'samtliga_lagenheter' ? 'rent_increase_percent' : 'apartment_increases',
                    },
                ]
                : [],
        };
    },
};
/** As the legacy `toLocaleString('sv-SE')`: the value's own decimals, at most three. */
const percentDigits = (value) => value == null ? 0 : Math.min(3, (String(value).split('.')[1] ?? '').length);
/**
 * "101 — 4,5 %" per line → two columns. With no rows entered (or on a blank
 * form) three empty rows to write on, as in the legacy template.
 */
function apartmentTable(input, blank) {
    const entered = blank
        ? []
        : (str(input.apartment_increases) ?? '')
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => line !== '')
            .map((line) => {
            const [apartment, ...rest] = line.split(/\s*[—–-]\s*/);
            return [apartment ?? '', rest.join(' — ')];
        });
    return table(['Lägenhetsnummer', 'Höjning i procent'], (entered.length > 0 ? entered : [['', ''], ['', ''], ['', '']]).map((cells) => [
        [p([val(cells[0] === '' ? null : cells[0], { blankWidth: 'medium' })])],
        [p([val(cells[1] === '' ? null : cells[1], { blankWidth: 'short' })])],
    ]), { variant: 'plain', repeatTableHeader: true });
}
/**
 * IMD tariffs are optional; the sub-block hides itself when none are set.
 * Each tariff prints as the legacy "Label: value unit" line.
 */
function imdBlock(input, blank) {
    const entries = [
        ['Varmvatten', slotText(input.imd_water), 'kr/m³'],
        ['Värme', slotText(input.imd_heat), ''],
        ['El', slotText(input.imd_electricity), ''],
    ];
    if (!blank && entries.every(([, value]) => value == null))
        return null;
    return group(entries.map(([label, value, unit], index) => p([
        t(`${label}: `),
        val(value == null ? null : `${value}${unit ? ` ${unit}` : ''}`, {
            blankWidth: 'medium',
            sourceKey: ['imd_water', 'imd_heat', 'imd_electricity'][index],
        }),
    ])), { variant: 'indent', title: 'Revidering av IMD-debitering enligt följande (om tillämpligt):' });
}
function missingIncreaseDetail(input, type, mode) {
    if (mode === 'blank')
        return false;
    if (type === 'samtliga_lagenheter') {
        return slotNumber(input.rent_increase_percent) == null || !str(input.effective_date);
    }
    return !str(input.apartment_increases);
}
/** Optional last point; omitted unless something is entered (always shown on a blank form). */
export const koppladeAvtal = {
    ...LEGAL,
    blockKey: 'fok.kopplade-avtal',
    version: 'v1',
    title: 'Eventuella kopplade avtal',
    summary: 'Optional reference to linked agreements such as tillvalsavtal or bilagor.',
    slots: [
        {
            key: 'linked_agreements',
            label: 'Kopplade avtal',
            type: 'textarea',
            help: 'Hänvisning till t.ex. tillvalsavtal eller bilagor. Lämna tomt om inga.',
        },
    ],
    compile: (input, ctx) => {
        const text = str(input.linked_agreements);
        if (!text && ctx.mode !== 'blank')
            return null;
        return [
            section('fok-kopplade-avtal', 'Eventuella kopplade avtal', [p([textVal(input, 'linked_agreements')])], {
                keepTogether: true,
                variant: 'clause',
            }),
        ];
    },
};
export const FOK_BLOCKS = [hus, overenskommelse, koppladeAvtal];
//# sourceMappingURL=blocks.js.map